import _round from 'lodash/round';
import { startOfQuarter } from 'date-fns';

import {
    detectionIsOn,
    getCurrentQuarter,
    shouldRenderDetectionStatusChange,
} from '@trqk/utils';

/**
 * Map a list of DetectionStatusChange elements into an array of DetectionStatusDateRange elements,
 * which will be an array of continuous date ranges that have `active: true` if detection was active
 * or `active: false` if not.
 * @param {DetectionStatusChange[]} detectionHistory
 */
export const getStatusRanges = (
    detectionHistory: DetectionStatusChange[],
): DetectionStatusDateRange[] => {
    const now = new Date();

    const statusRanges = detectionHistory
        .filter((statusChange: DetectionStatusChange): boolean =>
            shouldRenderDetectionStatusChange(statusChange),
        )
        .reduce(
            (
                acc: DetectionStatusDateRange[],
                { status, timestamp }: DetectionStatusChange,
                index: number,
            ): DetectionStatusDateRange[] => {
                const priorStatusChange =
                    index > 0 ? detectionHistory[index - 1] : null;

                const lastDateRange =
                    acc.length === 0 ? null : acc[acc.length - 1];

                if (lastDateRange?.end === null) {
                    acc[acc.length - 1].end = new Date(timestamp);
                }
                if (
                    detectionIsOn(status) &&
                    !detectionIsOn(priorStatusChange?.status ?? null)
                ) {
                    acc.push({
                        start: new Date(timestamp),
                        end: null,
                        active: true,
                    });
                } else {
                    acc.push({
                        start: new Date(timestamp),
                        end: null,
                        active: false,
                    });
                }

                return acc;
            },
            [] as DetectionStatusDateRange[],
        );

    if (
        statusRanges.length > 0 &&
        statusRanges[statusRanges.length - 1].end === null
    ) {
        // Add an explicit end date (set to now) for the last range
        statusRanges[statusRanges.length - 1].end = now;
    }

    if (statusRanges.length > 0) {
        const firstRangeStart = statusRanges[0]?.start;

        if (firstRangeStart) {
            const firstRangeQuarterStart = startOfQuarter(firstRangeStart);

            if (firstRangeStart > firstRangeQuarterStart) {
                // We need to make sure the first range has a start date set to the start of the
                // quarter
                if (statusRanges[0].active) {
                    // If the current first range is active, we can insert an inactive range before
                    // it
                    statusRanges.unshift({
                        start: firstRangeQuarterStart,
                        end: firstRangeStart,
                        active: false,
                    });
                } else {
                    // If the current first range is not active, we set its start date to the start
                    // of the quarter
                    statusRanges[0].start = firstRangeQuarterStart;
                }
            }
        }
    }

    return statusRanges;
};

/**
 * Map an array of DetectionStatusDateRange elements into an array of DetectionStatusMeterChunk
 * elements, which represent the date ranges as a percentage of the current quarter and include
 * details of whether each chunk had detection active and whether it occurs in the future. For any
 * given list, only the last chunk will occur in the future and will always have `active: false`.
 * @param statusRanges
 */
export const getStatusMeterChunks = (
    statusRanges: DetectionStatusDateRange[],
): DetectionStatusMeterChunk[] => {
    const currentQuarter = getCurrentQuarter();

    const currentQuarterDuration =
        currentQuarter.end.getTime() - currentQuarter.start.getTime();

    const statusRangesInThisQuarter = statusRanges.filter(
        ({ start, end }: DetectionStatusDateRange): boolean => {
            if (start === null || end === null || end < currentQuarter.start) {
                return false;
            }

            return true;
        },
    );

    let chunkTotalPercent = 0;

    const chunks = statusRangesInThisQuarter.reduce(
        (
            acc: DetectionStatusMeterChunk[],
            { start, end, active }: DetectionStatusDateRange,
        ): DetectionStatusMeterChunk[] => {
            if (start !== null && end !== null) {
                let effectiveStart = start;

                if (start < currentQuarter.start) {
                    effectiveStart = currentQuarter.start;
                }

                const chunkDuration = end.getTime() - effectiveStart.getTime();
                // Using absolute value helps prevent `percent` from being set to -0
                const percentFloat = Math.abs(
                    (chunkDuration / currentQuarterDuration) * 100,
                );

                const percent = _round(percentFloat, 0);

                chunkTotalPercent += percent;

                acc.push({
                    id: effectiveStart.toISOString(),
                    active,
                    percent,
                    future: false,
                });
            }

            return acc;
        },
        [] as DetectionStatusMeterChunk[],
    );

    const remainingPercent = 100 - chunkTotalPercent;

    if (remainingPercent > 0) {
        chunks.push({
            id: new Date().toISOString(),
            active: false,
            percent: remainingPercent,
            future: true,
        });
    }

    return chunks;
};
